<template>
  <!-- Loading principal de la aplicación -->
  <div v-if="!isAppReady" class="app-loading-container">
    <div class="app-loading-content">
      <!-- Logo animado central -->
      <div class="logo-spinner-container">
        <div class="logo-rings">
          <div class="logo-ring"></div>
          <div class="logo-ring"></div>
          <div class="logo-ring"></div>
        </div>
        <div class="logo-center">
          <img alt="Vue logo" class="logo-animated" src="@/assets/leon.png" width="60" height="60" />
        </div>
      </div>

      <!-- Texto de carga animado -->
      <div class="app-loading-text">
        <h2 class="app-loading-title">Inicializando aplicación</h2>
        <div class="app-loading-subtitle">Conectando con nuestros servidores</div>
        <div class="app-loading-dots">
          <span class="app-dot"></span>
          <span class="app-dot"></span>
          <span class="app-dot"></span>
        </div>
      </div>

      <!-- Barra de progreso elegante -->
      <div class="app-progress-container">
        <div class="app-progress-track">
          <div class="app-progress-fill"></div>
        </div>
        <div class="app-progress-glow"></div>
      </div>

      <!-- Número de versión en loading -->
      <div class="app-version-loading" v-if="appVersion">
        v{{ appVersion }}
      </div>

      <!-- Elementos flotantes decorativos -->
      <div class="app-floating-elements">
        <div class="app-floating-particle" style="--delay: 0s; --x: 15%; --y: 20%; --size: 4px;"></div>
        <div class="app-floating-particle" style="--delay: 0.8s; --x: 85%; --y: 25%; --size: 6px;"></div>
        <div class="app-floating-particle" style="--delay: 1.6s; --x: 70%; --y: 75%; --size: 3px;"></div>
        <div class="app-floating-particle" style="--delay: 2.4s; --x: 25%; --y: 80%; --size: 5px;"></div>
        <div class="app-floating-particle" style="--delay: 1.2s; --x: 90%; --y: 60%; --size: 4px;"></div>
        <div class="app-floating-particle" style="--delay: 2s; --x: 10%; --y: 70%; --size: 3px;"></div>
      </div>

      <!-- Ondas de fondo -->
      <div class="app-background-waves">
        <div class="app-wave"></div>
        <div class="app-wave"></div>
        <div class="app-wave"></div>
      </div>
    </div>
  </div>

  <!-- Contenido principal de la app -->
  <div v-else class="app-main-content">
    <!-- Header con logo centrado y versión -->
    <header class="app-header">
      <div class="header-content">
        <img alt="Vue logo" class="logo" src="@/assets/leon.png" width="40" height="40" />
        <span class="app-version-header" v-if="appVersion">v{{ appVersion }}</span>
      </div>
    </header>

    <div class="router-view">
      <RouterView />
    </div>

    <PWAInstallModal />
    <NotificationsPromptModal />
    <VerificationPendingModal :visible="showVerificationModal" @close="showVerificationModal = false" />

    <!-- Navbar móvil en la parte inferior -->
    <Navbar :user="user" />
  </div>

</template>

<script setup lang="ts">
import { ref, onMounted, provide, onUnmounted, computed, nextTick } from 'vue';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { useRouter } from 'vue-router';
import { RouterView } from 'vue-router';
import { auth, dbFirestore } from './firebase.config';
import Navbar from './components/Navbar.vue';
import { doc, getDoc, query, collection, where, limit, getDocs, onSnapshot, addDoc, updateDoc, orderBy, deleteDoc } from 'firebase/firestore';
import { getMessaging, getToken } from 'firebase/messaging';
import { app } from './firebase.config.ts';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { isModalOpen } from '@/composables/useModalState';
import { useEventBus } from '@/composables/useEventBus';
import PWAInstallModal from './components/PWAInstallModal.vue';
import NotificationsPromptModal from './components/NotificationsPromptModal.vue';
import VerificationPendingModal from './components/VerificationPendingModal.vue';
import type { UserSession } from './types/UserSession.ts';

const user = ref<User & Record<string, any> | null>(null);
const user_data = ref<Record<string, any> | null>(null);
const userRoles = ref<string[]>([]);
const isAuthReady = ref(false);
const minimumLoadingTimePassed = ref(false);
const showVerificationModal = ref(false);
const appVersion = ref<string | null>(null);

const domReady = ref(false);
const isAppReady = computed(() => isAuthReady.value && domReady.value);;
const router = useRouter();
const currentSessionId = ref<string | null>(null);

// Control de frecuencia de actualización
const lastActivityUpdate = ref<number>(0);
const ACTIVITY_UPDATE_THROTTLE = 30000; // 30 segundos
const SESSION_TIMEOUT = 4 * 60 * 60 * 1000; // 4 horas en lugar de 2 horas

provide('user', user);
provide('user_data', user_data);
provide('userRoles', userRoles);
provide('isAuthReady', isAuthReady);
provide('currentSessionId', currentSessionId);
provide('appVersion', appVersion);

let userDocUnsubscribe: (() => void) | null = null;
let sessionUnsubscribe: (() => void) | null = null;
let activityInterval: ReturnType<typeof setInterval> | null = null;

// Función para obtener la versión desde config.json
const loadAppVersion = async () => {
  try {
    const response = await fetch('/config.json', {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    if (response.ok) {
      const config = await response.json();
      appVersion.value = config.version || null;
      console.log('Versión de la app cargada:', appVersion.value);
    }
  } catch (error) {
    console.error('Error cargando versión de la app:', error);
  }
};

// Función para obtener información del dispositivo
const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screen: {
      width: window.screen.width,
      height: window.screen.height
    }
  };
};

// Función para crear una nueva sesión (mejorada)
const createUserSession = async (user: User, fcmToken: string | null = null): Promise<string | null> => {
  try {
    // Primero verificar si ya existe una sesión reutilizable para este usuario y dispositivo
    const existingSession = await checkExistingSession(user, fcmToken);
    if (existingSession) {
      console.log('Reutilizando sesión existente:', existingSession);
      return existingSession;
    }

    console.log(existingSession)
    const sessionData: UserSession = {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      loginTime: new Date(),
      deviceInfo: getDeviceInfo(),
      fcmToken: fcmToken || '',
      lastActivity: new Date(),
      heartbeat: Date.now()
    };

    const sessionRef = await addDoc(collection(dbFirestore, 'user_sessions'), sessionData);
    console.log('Nueva sesión creada:', sessionRef.id);
    return sessionRef.id;
  } catch (error) {
    console.error('Error creating user session:', error);
    return null;
  }
};

// Función para verificar y recuperar sesiones existentes (CORREGIDA)
const checkExistingSession = async (user: User, fcmToken: string | null): Promise<string | null> => {
  try {
    const deviceInfo = getDeviceInfo();
    // Buscar la sesión más reciente del usuario para este dispositivo
    const sessionsQuery = query(
      collection(dbFirestore, 'user_sessions'),
      where('uid', '==', user.uid),
      where('deviceInfo.userAgent', '==', deviceInfo.userAgent),
      orderBy('loginTime', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(sessionsQuery);

    if (!querySnapshot.empty) {
      const existingSession = querySnapshot.docs[0];
      const sessionData = existingSession.data();

      // Verificar si la sesión es reutilizable (dentro del tiempo límite)
      const lastActivity = sessionData.lastActivity?.toDate() || sessionData.loginTime?.toDate();
      const sessionTimeout = new Date(Date.now() - SESSION_TIMEOUT);

      if (lastActivity && lastActivity > sessionTimeout) {
        console.log('Sesión reutilizable encontrada para este dispositivo:', existingSession.id);
        // Reactivar la sesión existente
        const now = new Date();
        await updateDoc(existingSession.ref, {
          lastActivity: now,
          heartbeat: Date.now(),
          // No actualizar loginTime para mantener el tiempo original de inicio
          // Opcional: actualizar FCM token si cambió
          ...(fcmToken && { fcmToken })
        });

        return existingSession.id;
      } else {
        // La sesión es muy antigua, finalizarla correctamente si no está ya finalizada
        if (!sessionData.logoutTime) {
          const logoutTime = lastActivity || new Date();
          const loginTime = sessionData.loginTime?.toDate() || new Date();
          const duration = Math.floor((logoutTime.getTime() - loginTime.getTime()) / 1000);

          await updateDoc(existingSession.ref, {
            logoutTime: logoutTime,
            duration: duration
          });
          console.log('Sesión antigua de este dispositivo finalizada correctamente');
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error checking existing session:', error);
    return null;
  }
};

// Función para actualizar la última actividad (optimizada)
const updateLastActivity = async (sessionId: string | null) => {
  if (!sessionId) return;

  // Throttling: Solo actualizar cada 30 segundos
  const now = Date.now();
  if (now - lastActivityUpdate.value < ACTIVITY_UPDATE_THROTTLE) {
    return;
  }

  try {
    const sessionRef = doc(dbFirestore, 'user_sessions', sessionId);

    await updateDoc(sessionRef, {
      lastActivity: new Date(),
      heartbeat: now
    });

    lastActivityUpdate.value = now;
  } catch (error) {
    console.error('Error updating last activity:', error);
  }
};

// Función para ELIMINAR la sesión al cerrar sesión
const deleteSessionOnLogout = async (sessionId: string | null) => {
  if (!sessionId) return;

  try {
    const sessionRef = doc(dbFirestore, 'user_sessions', sessionId);
    await deleteDoc(sessionRef);
    console.log('Sesión eliminada en logout:', sessionId);
  } catch (error) {
    console.error('Error deleting session on logout:', error);
  }
};

// Sistema de heartbeat para mantener la sesión activa
const setupHeartbeatSystem = () => {
  if (activityInterval) {
    clearInterval(activityInterval);
  }

  activityInterval = setInterval(async () => {
    if (currentSessionId.value && user.value) {
      try {
        const sessionRef = doc(dbFirestore, 'user_sessions', currentSessionId.value);
        const sessionDoc = await getDoc(sessionRef);

        if (sessionDoc.exists()) {
          await updateDoc(sessionRef, {
            heartbeat: Date.now(),
            lastActivity: new Date()
          });
        }
      } catch (error) {
        console.error('Error en heartbeat:', error);
      }
    }
  }, 60000); // Cada minuto
};

// Configurar tracking de actividad optimizado
const setupActivityTracking = () => {
  const activityEvents = ['mousedown', 'keydown', 'touchstart'];

  const throttledUpdate = () => {
    if (currentSessionId.value) {
      // MANTENER SERVICE WORKER ACTUALIZADO
      // updateSessionStatusInSW(true);
    }
  };

  activityEvents.forEach(event => {
    window.addEventListener(event, throttledUpdate, { passive: true });
  });

  return () => {
    activityEvents.forEach(event => {
      window.removeEventListener(event, throttledUpdate);
    });
  };
};

const getAndSaveFCMToken = async (): Promise<string | null> => {
  try {
    if (Capacitor.isNativePlatform()) {
      const permissionResult = await PushNotifications.requestPermissions();
      if (permissionResult.receive === 'granted') {
        await PushNotifications.register();
        // En Capacitor, el token se obtiene a través del listener
        return null; // El token se maneja en el listener de Capacitor
      }
    } else {
      // PARA WEB - Usar directamente el Service Worker registrado por VitePWA
      if (Notification.permission !== 'granted') {
        console.log('Notification permission has not been granted.');
        return null;
      }

      const messaging = getMessaging(app);

      // Obtener el Service Worker registration activo
      const registration = await navigator.serviceWorker.ready;

      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      console.log("FCM Token obtenido:", token);
      return token;
    }
  } catch (error) {
    console.error('Error getting/saving FCM token:', error);
  }
  return null;
};

// Manejar cierre de la aplicación
const setupBeforeUnload = () => {
  window.addEventListener('beforeunload', () => {
    // Ya no es necesario hacer nada aquí. La ausencia de heartbeats
    // se encargará de marcar la sesión como inactiva.
  });
};

const updateSessionStatusInSW = async (isActive: boolean) => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage({
          type: 'SESSION_STATUS_UPDATE',
          isActive
        });
        console.log('Estado de sesión enviado al Service Worker:', isActive);
      }
    } catch (error) {
      console.error('Error enviando estado de sesión al SW:', error);
    }
  }
};

onMounted(async () => {
  // Cargar versión de la aplicación al inicio
  await loadAppVersion();

  // Enforce a minimum loading time of 3 seconds
  setTimeout(() => {
    minimumLoadingTimePassed.value = true;
  }, 3000);

  const { emit } = useEventBus();
  setupBeforeUnload();

  // Escuchar mensajes del Service Worker sobre actualizaciones
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
        console.log('Nueva versión disponible:', event.data.version);
        console.log('Versión anterior:', event.data.oldVersion);

        // Esperar a que el nuevo SW se active y luego recargar
        navigator.serviceWorker.ready.then((registration) => {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }

          // Recargar después de que el nuevo SW tome control
          let refreshing = false;
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (!refreshing) {
              refreshing = true;
              window.location.reload();
            }
          });
        });
      }
    });
  }

  // Handle foreground notifications
  if (Capacitor.isNativePlatform()) {
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received in foreground: ', notification);
      LocalNotifications.schedule({
        notifications: [
          {
            title: notification.title || 'Notificación',
            body: notification.body || '',
            id: new Date().getTime(),
            extra: notification.data,
          },
        ],
      });
    });
  }

  if (Capacitor.isNativePlatform()) {
    CapacitorApp.addListener('backButton', () => {
      if (isModalOpen.value) {
        emit('close-active-modal');
      } else if (router.currentRoute.value.path !== '/') {
        router.push('/');
      } else {
        const confirmation = confirm('¿Estás seguro de que quieres salir de la aplicación?');
        if (confirmation) {
          CapacitorApp.exitApp();
        }
      }
    });
  }

  onAuthStateChanged(auth, async (currentUser) => {
    // Limpiar suscripciones anteriores
    if (userDocUnsubscribe) {
      userDocUnsubscribe();
      userDocUnsubscribe = null;
    }

    isAuthReady.value = true;
    if (activityInterval) {
      clearInterval(activityInterval);
      activityInterval = null;
    }

    // Solo cerrar sesión anterior si es un cambio de usuario, no una reconexión
    if (currentSessionId.value && user.value && currentUser && user.value.uid !== currentUser.uid) {
      // Al cambiar de usuario, el estado de autenticación ya es el del nuevo usuario,
      // por lo que no podemos actualizar la sesión del usuario anterior por falta de permisos.
      currentSessionId.value = null;
    }

    if (currentUser) {
      user.value = currentUser;
      try {
        const userQuery = query(collection(dbFirestore, 'users'), where('uid', "==", currentUser.uid), limit(1));
        const userQuerySnapshot = await getDocs(userQuery);

        if (!userQuerySnapshot.empty) {
          const userDataDoc = userQuerySnapshot.docs[0];
          const token = await getAndSaveFCMToken();
          // Crear o recuperar sesión
          const sessionId = await createUserSession(currentUser, token);

          if (sessionId) {
            currentSessionId.value = sessionId;

            // Stop listening to previous session changes
            if (sessionUnsubscribe) {
              sessionUnsubscribe();
            }
            // Listen for remote deletion of the current session
            const sessionRef = doc(dbFirestore, 'user_sessions', sessionId);
            sessionUnsubscribe = onSnapshot(sessionRef, (docSnap) => {
              if (!docSnap.exists()) {
                console.warn('La sesión actual fue cerrada remotamente. Cerrando sesión.');
                handleLogout();
              }
            });

            // Configurar sistemas de mantenimiento de sesión
            setupHeartbeatSystem();
            const cleanupActivityTracking = setupActivityTracking();

            // Actualizar inmediatamente al iniciar
            updateLastActivity(sessionId);
          }

          // Mantener la suscripción a los datos del usuario
          userDocUnsubscribe = onSnapshot(userDataDoc.ref, (userSnap) => {
            if (userSnap.exists()) {
              const data = userSnap.data();
              user_data.value = data;
              showVerificationModal.value = !user_data.value?.verified;
              userRoles.value = data.roles || [];
              if (data.verified === false && !sessionStorage.getItem('verificationModalShown')) {
                emit('show-verification-modal');
                sessionStorage.setItem('verificationModalShown', 'true');
              }
            } else {
              user_data.value = {};
              userRoles.value = [];
            }
          });
        }

        // ACTUALIZAR SERVICE WORKER CON ESTADO DE SESIÓN
        await updateSessionStatusInSW(true);

      } catch (error) {
        console.error('Error en onAuthStateChanged:', error);
        user_data.value = {};
        userRoles.value = [];
      }
    } else {
      // Solo cerrar sesión si realmente estamos haciendo logout
      if (currentSessionId.value && user.value) {
        // En este punto, el usuario ya no está autenticado y no podemos actualizar la sesión.
        // El `handleLogout` se encarga de actualizar la sesión ANTES de cerrar la sesión.
        currentSessionId.value = null;
      }

      user.value = null;
      user_data.value = {};
      userRoles.value = [];
    }
  });

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage({
          type: 'CHECK_VERSION'
        });
      }
    } catch (error) {
      console.error('Error solicitando verificación de versión:', error);
    }
  }

  nextTick(() => {
    domReady.value = true;
  });
});

// Función de logout (MEJORADA)
const handleLogout = async () => {
  sessionStorage.removeItem('verificationModalShown');
  // Unsubscribe from session listener to prevent loops
  if (sessionUnsubscribe) {
    sessionUnsubscribe();
    sessionUnsubscribe = null;
  }

  if (currentSessionId.value && user.value) {
    // This might be called after the doc is already deleted, which is fine.
    await deleteSessionOnLogout(currentSessionId.value);
    currentSessionId.value = null;
  }

  if (activityInterval) {
    clearInterval(activityInterval);
    activityInterval = null;
  }

  // NOTIFICAR SERVICE WORKER DEL LOGOUT
  await updateSessionStatusInSW(false);

  // Only sign out if a user is currently signed in
  if (auth.currentUser) {
    await signOut(auth);
  }

  // Avoid pushing to router if already there
  if (router.currentRoute.value.path !== '/') {
    router.push('/');
  }
};

provide('handleLogout', handleLogout);

const forceCloseSession = async (sessionId: string) => {
  if (!userRoles.value.includes('admin')) {
    console.error('Permission denied: only admins can force close sessions.');
    return;
  }
  if (!sessionId) return;
  try {
    const sessionRef = doc(dbFirestore, 'user_sessions', sessionId);
    await deleteDoc(sessionRef);
    console.log(`Session forcefully closed: ${sessionId}`);
  } catch (error) {
    console.error(`Error force closing session ${sessionId}:`, error);
  }
};
provide('forceCloseSession', forceCloseSession);

// Cleanup al desmontar
onUnmounted(() => {
  if (activityInterval) {
    clearInterval(activityInterval);
  }
  if (sessionUnsubscribe) {
    sessionUnsubscribe();
  }
});
</script>

<style scoped>
/* ===== LOADING PRINCIPAL DE LA APLICACIÓN ===== */
.app-loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%); */
  background: linear-gradient(135deg, #425bb8 0%, #070c54 50%, #425bb8 100%);
  /* background: linear-gradient(135deg, #425bb8 0%, #070c54 100%); */
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.app-loading-content {
  text-align: center;
  color: white;
  z-index: 2;
  position: relative;
}

/* Logo central con anillos giratorios */
.logo-spinner-container {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-rings {
  position: absolute;
  width: 100%;
  height: 100%;
}

.logo-ring {
  position: absolute;
  border: 2px solid transparent;
  border-radius: 50%;
  animation: logoSpin 3s linear infinite;
}

.logo-ring:nth-child(1) {
  width: 100%;
  height: 100%;
  border-top-color: rgba(255, 255, 255, 0.8);
  border-right-color: rgba(255, 255, 255, 0.4);
  animation-duration: 3s;
}

.logo-ring:nth-child(2) {
  width: 85%;
  height: 85%;
  top: 7.5%;
  left: 7.5%;
  border-top-color: rgba(255, 255, 255, 0.6);
  border-left-color: rgba(255, 255, 255, 0.3);
  animation-duration: 2s;
  animation-direction: reverse;
}

.logo-ring:nth-child(3) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: rgba(255, 255, 255, 0.4);
  border-bottom-color: rgba(255, 255, 255, 0.2);
  animation-duration: 1.5s;
}

.logo-center {
  position: relative;
  z-index: 3;
  animation: logoFloat 3s ease-in-out infinite;
}

.logo-animated {
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

@keyframes logoSpin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes logoFloat {

  0%,
  100% {
    transform: translateY(0px) scale(1);
    filter: brightness(1);
  }

  50% {
    transform: translateY(-10px) scale(1.05);
    filter: brightness(1.2);
  }
}

/* Texto de carga animado */
.app-loading-text {
  margin-bottom: 2.5rem;
}

.app-loading-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
  animation: textGlow 2s ease-in-out infinite alternate;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

.app-loading-subtitle {
  font-size: 1rem;
  opacity: 0.85;
  margin-bottom: 1.5rem;
  animation: fadeInOut 3s ease-in-out infinite;
}

@keyframes textGlow {
  0% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  }

  100% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.8), 0 0 30px rgba(255, 255, 255, 0.4);
  }
}

@keyframes fadeInOut {

  0%,
  100% {
    opacity: 0.6;
  }

  50% {
    opacity: 1;
  }
}

/* Dots animados */
.app-loading-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.app-dot {
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  animation: dotBounce 1.4s ease-in-out infinite both;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.6);
}

.app-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.app-dot:nth-child(2) {
  animation-delay: -0.16s;
}

.app-dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes dotBounce {

  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }

  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

/* Barra de progreso elegante */
.app-progress-container {
  position: relative;
  width: 280px;
  height: 6px;
  margin: 0 auto 3rem;
}

.app-progress-track {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.app-progress-fill {
  height: 100%;
  background: linear-gradient(90deg,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 0.8) 100%);
  border-radius: 3px;
  animation: progressFlow 2.5s ease-in-out infinite;
  transform-origin: left;
}

.app-progress-glow {
  position: absolute;
  top: -2px;
  left: 0;
  width: 100%;
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  animation: progressGlow 2.5s ease-in-out infinite;
}

@keyframes progressFlow {
  0% {
    width: 0%;
  }

  50% {
    width: 70%;
  }

  100% {
    width: 100%;
  }
}

@keyframes progressGlow {

  0%,
  100% {
    opacity: 0.3;
  }

  50% {
    opacity: 0.8;
  }
}

/* Partículas flotantes */
.app-floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.app-floating-particle {
  position: absolute;
  width: var(--size);
  height: var(--size);
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: particleFloat 8s ease-in-out infinite;
  animation-delay: var(--delay);
  left: var(--x);
  top: var(--y);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

@keyframes particleFloat {

  0%,
  100% {
    transform: translateY(0px) translateX(0px) scale(1);
    opacity: 0.4;
  }

  25% {
    transform: translateY(-30px) translateX(10px) scale(1.2);
    opacity: 0.8;
  }

  50% {
    transform: translateY(-20px) translateX(-15px) scale(0.8);
    opacity: 1;
  }

  75% {
    transform: translateY(-40px) translateX(5px) scale(1.1);
    opacity: 0.6;
  }
}

/* Ondas de fondo */
.app-background-waves {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.app-wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  animation: waveMove 6s ease-in-out infinite;
}

.app-wave:nth-child(1) {
  animation-duration: 6s;
  animation-delay: 0s;
  opacity: 0.3;
}

.app-wave:nth-child(2) {
  animation-duration: 8s;
  animation-delay: -2s;
  opacity: 0.2;
  height: 120px;
}

.app-wave:nth-child(3) {
  animation-duration: 10s;
  animation-delay: -4s;
  opacity: 0.1;
  height: 140px;
}

@keyframes waveMove {

  0%,
  100% {
    transform: translateX(-50%) translateY(50px);
  }

  50% {
    transform: translateX(-60%) translateY(30px);
  }
}


.app-main-content {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  animation: appFadeIn 0.8s ease-out;
}

@keyframes appFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header fijo arriba */
.app-header {
  background-color: #000000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 0.75rem 1rem;
  flex: 0 0 auto;
  z-index: 100;
  animation: headerSlideDown 0.6s ease-out 0.2s both;
}

@keyframes headerSlideDown {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-content {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.logo {
  display: block;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.1) rotate(5deg);
}

/* Contenido del router con scroll */
.router-view {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Navbar fijo abajo (asumiendo que tienes estos estilos en el componente Navbar) */
:global(.navbar-component) {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 100 !important;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 768px) {
  .app-loading-title {
    font-size: 1.5rem;
  }

  .app-loading-subtitle {
    font-size: 0.9rem;
  }

  .logo-spinner-container {
    width: 100px;
    height: 100px;
    margin-bottom: 2rem;
  }

  .logo-animated {
    width: 45px;
    height: 45px;
  }

  .app-progress-container {
    width: 220px;
  }

  .app-header {
    padding: 0.5rem;
  }

  /* .router-view {
    padding-top: 70px;
    padding-bottom: 70px;
  } */

  /* Partículas más pequeñas en móvil */
  .app-floating-particle {
    --size: 3px !important;
  }
}

@media (max-width: 480px) {
  .app-loading-title {
    font-size: 1.3rem;
  }

  .logo-spinner-container {
    width: 80px;
    height: 80px;
    margin-bottom: 1.5rem;
  }

  .logo-animated {
    width: 35px;
    height: 35px;
  }

  .app-progress-container {
    width: 180px;
    margin-bottom: 2rem;
  }

  .app-loading-text {
    margin-bottom: 2rem;
  }

  /* .router-view {
    padding-top: 60px;
    padding-bottom: 60px;
  } */
}

/* Estilos para escritorio */
@media (min-width: 769px) {
  .app-header {
    padding: 1rem;
  }

  .logo {
    width: 50px;
    height: 50px;
  }

  .app-loading-title {
    font-size: 2rem;
  }

  .logo-spinner-container {
    width: 160px;
    height: 160px;
  }

  .logo-animated {
    width: 70px;
    height: 70px;
  }

  /* .router-view {
    padding-top: 90px;
    padding-bottom: 80px;
  } */
}

/* Modo oscuro */
@media (prefers-color-scheme: dark) {
  .app-header {
    background-color: #1a1a1a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
}

/* Scrollbar personalizado para el contenido */
.router-view::-webkit-scrollbar {
  width: 6px;
}

.router-view::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.router-view::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.router-view::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Mejoras de rendimiento */
.app-loading-container,
.app-floating-particle,
.app-wave,
.logo-ring {
  will-change: transform, opacity;
  backface-visibility: hidden;
}

/* Prefers reduced motion */
@media (prefers-reduced-motion: reduce) {

  .app-loading-container,
  .logo-ring,
  .app-floating-particle,
  .app-wave,
  .logo-center,
  .app-dot,
  .app-progress-fill {
    animation: none;
  }

  .app-loading-container {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
}

/* Transiciones suaves */
.template-hetz * {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* Asegurar que no haya scroll horizontal */
.template-hetz {
  overflow-x: hidden;
}

.router-view {
  width: 100%;
  box-sizing: border-box;
}

/* ===== VERSIÓN DE LA APLICACIÓN ===== */
.app-version-loading {
  position: absolute;
  bottom: 2rem;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 0.8rem;
  opacity: 0.7;
  color: rgba(255, 255, 255, 0.8);
  animation: versionFade 3s ease-in-out infinite;
}

.app-version-header {
  position: absolute;
  right: 1rem;
  font-size: 0.75rem;
  opacity: 0.7;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

@keyframes versionFade {

  0%,
  100% {
    opacity: 0.5;
  }

  50% {
    opacity: 0.9;
  }
}

/* ===== MODALES ===== */
/* Estilos base para modales */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 90vw;
  width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s ease-out;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===== NAVBAR ===== */
/* Estilos base para navbar móvil */
.navbar-mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e0e0e0;
  padding: 0.5rem 1rem;
  z-index: 100;
  backdrop-filter: blur(10px);
}

.navbar-content {
  display: flex;
  justify-content: space-around;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
}

.navbar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.navbar-item.active {
  background: rgba(66, 91, 184, 0.1);
}

.navbar-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 0.25rem;
  transition: transform 0.3s ease;
}

.navbar-item:hover .navbar-icon {
  transform: scale(1.1);
}

.navbar-label {
  font-size: 0.7rem;
  color: #666;
  transition: color 0.3s ease;
}

.navbar-item.active .navbar-label {
  color: #425bb8;
  font-weight: 600;
}

/* ===== MEJORAS DE ACCESIBILIDAD ===== */
/* Focus visible para navegación por teclado */
.navbar-item:focus-visible {
  outline: 2px solid #425bb8;
  outline-offset: 2px;
}

.modal-content:focus-visible {
  outline: 2px solid #425bb8;
  outline-offset: 4px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .app-loading-container {
    background: linear-gradient(135deg, #000000 0%, #333333 100%);
  }

  .app-progress-fill {
    background: #ffffff;
  }

  .navbar-mobile {
    border-top: 2px solid #000000;
  }
}

/* ===== ESTADOS DE CARGA ESPECÍFICOS ===== */
/* Estado cuando la autenticación está lista pero el DOM no */
.app-loading-container.auth-ready {
  opacity: 0.8;
  transition: opacity 0.5s ease;
}

/* Estado cuando el DOM está listo pero la autenticación no */
.app-loading-container.dom-ready {
  animation-duration: 4s;
}

/* ===== ANIMACIONES DE ENTRADA MEJORADAS ===== */
.app-main-content {
  animation: contentEntrance 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

@keyframes contentEntrance {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== RESPONSIVE ADICIONAL ===== */
@media (max-height: 600px) {
  .app-loading-container {
    padding: 1rem;
  }

  .logo-spinner-container {
    width: 80px;
    height: 80px;
    margin-bottom: 1.5rem;
  }

  .app-loading-text {
    margin-bottom: 1.5rem;
  }

  .app-progress-container {
    margin-bottom: 1.5rem;
  }

  .app-version-loading {
    bottom: 1rem;
  }
}

/* Orientación landscape en móviles */
@media (max-width: 768px) and (orientation: landscape) {
  .app-loading-container {
    padding: 1rem;
  }

  .logo-spinner-container {
    width: 80px;
    height: 80px;
    margin-bottom: 1rem;
  }

  .app-loading-title {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
  }

  .app-loading-subtitle {
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }

  .app-progress-container {
    margin-bottom: 1.5rem;
  }
}

/* ===== MEJORAS DE RENDIMIENTO ===== */
/* Optimización de composición para elementos animados */
.logo-rings,
.app-floating-elements,
.app-background-waves {
  transform: translateZ(0);
}

/* Reduce repaints para elementos estáticos */
.app-header,
.navbar-mobile {
  transform: translateZ(0);
  will-change: transform;
}

/* ===== ESTADOS DE ERROR ===== */
/* Estilos para cuando hay error de carga */
.app-loading-container.error-state {
  background: linear-gradient(135deg, #ff6b6b 0%, #c44569 100%);
}

.app-loading-container.error-state .logo-animated {
  filter: brightness(0.8) saturate(0.8);
}

/* ===== DARK MODE MEJORADO ===== */
@media (prefers-color-scheme: dark) {
  .navbar-mobile {
    background: #1a1a1a;
    border-top-color: #333;
  }

  .navbar-label {
    color: #ccc;
  }

  .navbar-item.active {
    background: rgba(66, 91, 184, 0.2);
  }

  .navbar-item.active .navbar-label {
    color: #5c7cfa;
  }

  .modal-content {
    background: #2d2d2d;
    color: white;
  }
}

/* ===== IMPRESIÓN ===== */
@media print {

  .app-loading-container,
  .navbar-mobile,
  .app-header {
    display: none !important;
  }

  .app-main-content {
    height: auto;
    animation: none;
  }
}
</style>
